/** Stardrop — global gameplay, physics, and presentation constants */

export const GAME_NAME = 'Stardrop';
export const GAME_TAGLINE = 'Celestial Confections';

/** Number of merge tiers (0..TIER_COUNT-1) */
export const TIER_COUNT = 11;

/** Play container (world units) */
export const CONTAINER = {
  width: 7.6,
  height: 9.4,
  depth: 0.15,
  wallThickness: 0.32,
  floorThickness: 0.38,
  /** Slight outward bevel for soft wall feel */
  wallFriction: 0.55,
  floorFriction: 0.7,
  restitution: 0.12,
} as const;

/** Danger line Y (world space origin is container center) */
export const DANGER_LINE_Y = CONTAINER.height * 0.5 - 1.05;
export const DANGER_HOLD_TIME = 1.75; // seconds above line before lose
export const DANGER_WARN_TIME = 0.35;

/** Physics */
export const PHYSICS = {
  gravity: -18.5,
  timeStep: 1 / 60,
  maxSubsteps: 2,
  linearDamping: 0.28,
  angularDamping: 0.45,
  maxLinearVelocity: 22,
  maxAngularVelocity: 14,
  contactForceEvents: true,
  /** Base radius scale for tier 0 */
  baseRadius: 0.46,
  /** Radius growth per tier (multiplicative-ish via formula) */
  radiusGrowth: 1.255,
  massDensity: 1.15,
  restitutionBase: 0.18,
  restitutionDecay: 0.012,
  frictionBase: 0.48,
  frictionGrowth: 0.02,
  spawnHeight: CONTAINER.height * 0.5 + 0.85,
  dropCooldown: 0.38,
  mergeImpulseRadius: 1.8,
  mergeImpulseStrength: 0.55,
  mergeCooldown: 0.12,
  sleepLinearThreshold: 0.08,
  sleepAngularThreshold: 0.12,
  sleepTime: 0.5,
} as const;

/** Scoring */
export const SCORE = {
  /** Points for merging into tier t (result tier) */
  mergeBase: [1, 3, 6, 12, 24, 48, 96, 180, 320, 560, 1000],
  comboMultiplier: 0.35,
  chainBonus: 0.5,
  maxComboDisplay: 12,
} as const;

/** Drop / next-piece */
export const DROP = {
  /** Weighted random among first N tiers for spawn */
  spawnMaxTier: 4,
  weights: [32, 28, 22, 12, 6],
  aimSmoothing: 14,
  ghostOpacity: 0.55,
  guideLineOpacity: 0.35,
} as const;

/** Graphics quality presets */
export type QualityLevel = 'low' | 'medium' | 'high';

export const QUALITY: Record<
  QualityLevel,
  {
    shadows: boolean;
    shadowMapSize: number;
    particles: number;
    bloom: boolean;
    aa: boolean;
    pixelRatio: number;
  }
> = {
  low: { shadows: false, shadowMapSize: 512, particles: 40, bloom: false, aa: false, pixelRatio: 1 },
  medium: { shadows: true, shadowMapSize: 1024, particles: 100, bloom: true, aa: true, pixelRatio: 1.5 },
  high: { shadows: true, shadowMapSize: 2048, particles: 180, bloom: true, aa: true, pixelRatio: 2 },
};

export const STORAGE_KEYS = {
  highScores: 'stardrop_high_scores_v1',
  settings: 'stardrop_settings_v1',
  tutorialSeen: 'stardrop_tutorial_v1',
} as const;

export const COLORS = {
  bgDeep: 0x0d0618,
  bgMid: 0x1a0b2e,
  bgAccent: 0x2d1b4e,
  jarGlass: 0xc8b8ff,
  jarRim: 0xffd6f0,
  danger: 0xff4d6d,
  dangerSoft: 0xff8fa3,
  scoreGold: 0xffe29a,
  uiText: 0xfff8ff,
  uiMuted: 0xc9b8e8,
} as const;
